import { ChangeDetectionStrategy, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Router } from '@angular/router';

import * as path from 'path';
import { GradeFunctions } from 'libs/util';
import { isEmpty } from 'lodash';
import { BehaviorSubject, Observable, ReplaySubject, Subject, combineLatest } from 'rxjs';
import { debounceTime, delay, distinctUntilChanged, filter, map, take, takeUntil } from 'rxjs/operators';

import { BarcodeScanner } from '@app/src/app/barcodeScanner';
import { GroupsFacadeService } from '@app/src/app/groups/services';
import { RolesService } from '@app/src/app/roles/services';
import { GradesService } from '@app/src/app/services/Grades';
import { StudentIdService } from '@app/src/app/services/Student';
import { MingaSettingsService } from '@app/src/app/store/Minga/services';

import { XlsListUploaderDialogComponent } from '@shared/components/XlsListUploader/XlsListUploaderDialog';
import { FiltersFormData, FiltersFormService } from '@shared/components/filters-form';
import { FormSelectOption } from '@shared/components/form';
import { SystemAlertSnackBarService } from '@shared/components/system-alert-snackbar';
import { MediaBreakpoints, MediaService } from '@shared/services/media';
import QrCodeService from '@shared/services/qrcode';

import {
  AUTOSEARCH_DEBOUNCE_TIME,
  HYBRID_CAMERA_SCAN_SAME_ID_GAP,
  PS_ACCEPTED_FILE_TYPES,
  PsSearchMessages,
} from '../../constants';
import { PeopleSelectorFormService, PeopleSelectorService } from '../../services';
import { PsData, PsUploadRow } from '../../types';

const lastScannedValueSubject = new BehaviorSubject<{
  value: string;
  timestamp: number;
} | null>(null);

@Component({
  selector: 'mg-ps-search',
  templateUrl: './ps-search.component.html',
  styleUrls: ['./ps-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PsSearchComponent implements OnInit, OnDestroy {
  // Children

  @ViewChild('searchField') searchField: ElementRef<HTMLInputElement>;
  @ViewChild('fileInput', { static: true })
  fileInput?: ElementRef<HTMLInputElement>;

  // Constants

  public readonly MSG = PsSearchMessages;
  public readonly cameraAvailable = this._barCodeScanner.isAvailable();
  public readonly ACCEPTED_FILE_TYPES = PS_ACCEPTED_FILE_TYPES.join(',');
  public readonly IS_HYBRID_APP = !window.MINGA_APP_BROWSER;

  // Cleanup

  private readonly _destroyedSubject = new ReplaySubject<void>(1);

  // Focus events

  public keywordsFocusSubj = new Subject();
  public barCodeFocusSubj = new Subject();

  /** Misc */
  public sideBarTitle$: Observable<string>;
  public accordionOpenState: boolean;

  /** Filters Accordion Initial State */
  public readonly isDesktop$ = this.media.breakpoint$.pipe(
    takeUntil(this._destroyedSubject),
    map(bp => {
      const breakpoints: MediaBreakpoints[] = ['medium', 'medium-large', 'large', 'xlarge'];
      return breakpoints.includes(bp) ? true : false;
    }),
  );

  // Form

  public readonly searchFieldControl = this._fb.control('');
  public readonly barCodeFieldControl = this._fb.control('');

  /** Group Filter Options */
  public groupOptions$ = this._groupsFacade.getAllGroups().pipe(
    map(groups =>
      groups.map(group => ({
        label: group.name,
        value: group.hash,
      })),
    ),
  );

  /** Selected user lists */
  public selectedLists$: Observable<number[]>;

  /** Grade Filter Options */
  public readonly gradeOptions$: Observable<FormSelectOption<string>[]> = this._gradesService.grades$.pipe(
    takeUntil(this._destroyedSubject),
    map(grades =>
      grades.map(grade => ({
        label: grade,
        value: grade,
      })),
    ),
  );

  /** Role Filter Options */
  public readonly roleOptions$: Observable<FormSelectOption<string>[]> = this._rolesService.roles$.pipe(
    takeUntil(this._destroyedSubject),
    map(roles => {
      return roles
        .filter(role => role.name !== 'Super Admin')
        .map(role => ({
          label: role.name,
          value: role.name,
        }));
    }),
  );

  private _useQrCode = false;

  /** Filters Form Observables */
  public filtersFormStructure$: Observable<FiltersFormData>;
  public showMobileFiltersFormButton$: Observable<boolean>;

  // Inputs

  @Input() form: PeopleSelectorFormService;

  /** Component Constructor */
  constructor(
    public media: MediaService,
    public settingsService: MingaSettingsService,
    private _peopleSelector: PeopleSelectorService,
    private _systemSnack: SystemAlertSnackBarService,
    private _barCodeScanner: BarcodeScanner,
    private _fb: UntypedFormBuilder,
    private _groupsFacade: GroupsFacadeService,
    private _gradesService: GradesService,
    private _rolesService: RolesService,
    private _matDialog: MatDialog,
    private _filtersFormService: FiltersFormService,
    private _studentIdService: StudentIdService,
    private _qrCodeService: QrCodeService,
    private router: Router,
  ) {
    this._openCameraSub();
    this._studentIdService.useQrCode$.pipe(takeUntil(this._destroyedSubject)).subscribe(useQrCode => {
      this._useQrCode = useQrCode;
    });
  }

  ngOnInit(): void {
    if (!this.form) return;
    this._sidebarTitle();
    this._pageSwitchSub();
    this._searchInputSub();
    this._barCodeFocusSub();
    this._peopleSubmitSub();
    this._searchToolSwitchSub();
    this._focusBarcodeSubscription();

    this.selectedLists$ = this.form.filters.state$.pipe(map(state => (state.lists || []).map(Number)));
    this._setupFormFiltersObservables();
  }

  ngOnDestroy(): void {
    this._destroyedSubject.next();
    this._destroyedSubject.complete();
    this.keywordsFocusSubj.complete();
    this.barCodeFocusSubj.complete();
  }

  public handleApplyFilters() {
    this.form.search();
  }

  public async openCamera(): Promise<void> {
    this.searchFieldControl.reset();
    const pageType = this.form.pages.getActivePageType();
    try {
      const { cancelled = false, text: barcodeText = '' } = await this._barCodeScanner.scan(
        this._useQrCode ? ['qrcode'] : ['barcode'],
        this.form?.formTitle,
      );
      let text = barcodeText;

      if (this._useQrCode) {
        const qrData = this._qrCodeService.readIdQrCode(text);
        text = qrData.id;
        if (!text) throw new Error('Failed to read QR string');
      }

      if (cancelled || !text) return;

      const submissionFunction = async (val: string) => {
        if (pageType === 'search') {
          this.form.filters.set('keywords', val);
          await this.form.search('text');
          if (this.form.searchResults.size > 0) {
            this.form.selection.select([this.form.searchResults.dataSource.data[0]]);
            await this.form.submit('camera');
          } else {
            await this.form.onSearchScanNoResults(val, 'camera');
          }
        } else {
          const found = this.form.searchResults.dataSource.data.find(({ studentId }) => studentId === val);
          if (found) {
            this.form.selection.select([found]);
            await this.form.submit('camera');
          } else {
            await this.form.onListScanNoResults(val, 'camera');
          }
        }
      };
      if (this.IS_HYBRID_APP) {
        await this._handleHybridAppCameraScanSubmission(text, submissionFunction);
      } else {
        await submissionFunction(text);
      }
    } catch (err) {
      if (window.MINGA_APP_IOS) return;
      this._systemSnack.open({
        type: 'error',
        message: err?.message ?? 'Something went wrong when trying to access the camera.',
      });
    }
  }

  public async submitBarcodeInput() {
    const barCodeString = this._parseBarcodeScanData(this.barCodeFieldControl?.value?.trim() ?? '');

    const pageType = this.form.pages.getActivePageType();
    this.barCodeFieldControl.reset();

    if (barCodeString.length <= 2) return;

    let found: PsData;
    if (pageType === 'search') {
      this.form.filters.set('keywords', barCodeString);
      await this.form.search();
      found = this.form.searchResults.dataSource.data[0];
      if (!found) this.form.onSearchScanNoResults(barCodeString, 'barcode');
    } else {
      found = this.form.searchResults.dataSource.data.find(({ studentId }) => studentId === barCodeString);
      if (!found) await this.form.onListScanNoResults(barCodeString, 'barcode');
    }
    if (found) {
      this.form.selection.clear();
      this.form.selection.select([found]);
      await this.form.submit('barcode');
    }
  }

  public async toggleBarCode() {
    const currentMode = this.form.getCurrentSearchMode();
    this.form.setSearchMode(currentMode !== 'barcode' ? 'barcode' : 'keywords');
  }

  public uploadFile(event: Event) {
    try {
      const element = event.currentTarget as HTMLInputElement;
      const file: File | null = element.files[0];
      const extname = path.extname(file.name);
      if (!PS_ACCEPTED_FILE_TYPES.includes(extname)) {
        throw new Error('Please upload an Excel (.xlsx) or a Comma-separated Values (.csv) file');
      }
      const data = this.form.filters.setFile(file);
      const dialogRef = this._matDialog.open(XlsListUploaderDialogComponent, {
        data,
        panelClass: 'mg-no-padding-dialog',
      });
      dialogRef
        .afterClosed()
        .pipe(takeUntil(this._destroyedSubject))
        .subscribe((res: PsUploadRow[] | undefined) => {
          if (!res) {
            this.form.filters.clear('file');
          } else {
            this.form.filters.set('fileUploadedPeople', res);
            this.form.search('file');
          }
        });
    } catch (error) {
      this._systemSnack.open({
        message: error.message,
        type: 'error',
      });
    }
  }

  public async updateFromFiltersForm(state: any) {
    this.form.filters.set('groups', state?.group);
    this.form.filters.set('grades', state?.grades);
    this.form.filters.set('roles', state?.roles);
    this.setUserListFilter(state?.['user-lists']);
    await this.form.search();
  }

  private async _handleHybridAppCameraScanSubmission(value: string, callBackFunction: (v) => Promise<void>) {
    const lastScannedValue = lastScannedValueSubject.getValue();
    const currentTimeStamp = new Date().getTime();
    const saveAndSubmit = async () => {
      lastScannedValueSubject.next({ value, timestamp: currentTimeStamp });
      await callBackFunction(value);
    };
    if (lastScannedValue == null || lastScannedValue?.value !== value) {
      await saveAndSubmit();
    } else {
      const timeDiff = currentTimeStamp - lastScannedValue?.timestamp;
      if (timeDiff > HYBRID_CAMERA_SCAN_SAME_ID_GAP) {
        await saveAndSubmit();
      } else {
        const ref = await this._systemSnack.error('Please wait before scanning the same id again');
        await new Promise(resolve => setTimeout(resolve, 2000));
        ref.dismiss();
        this.openCamera();
      }
    }
  }

  private _sidebarTitle() {
    this.sideBarTitle$ = combineLatest([this.form.filters.hasLoadedFile$, this.form.pages.activePageType$]).pipe(
      takeUntil(this._destroyedSubject),
      map(([hasFile, pageType]) => {
        if (pageType === 'list') return PsSearchMessages.LIST_TITLE;
        return hasFile ? PsSearchMessages.FILE_TITLE : PsSearchMessages.TITLE;
      }),
    );
  }

  private _searchInputSub() {
    return this.searchFieldControl.valueChanges
      .pipe(distinctUntilChanged(), debounceTime(AUTOSEARCH_DEBOUNCE_TIME))
      .subscribe(async text => {
        if (typeof text === 'string') {
          this.form.filters.set('keywords', text.trim().toLowerCase());
        }
        if (!text) return;
        const pageType = this.form.pages.getActivePageType();
        if (pageType === 'search') await this.form.search();
      });
  }

  private _barCodeFocusSub() {
    return this.form.searchMode$
      .pipe(
        takeUntil(this._destroyedSubject),
        filter(val => val === 'barcode'),
        delay(100),
      )
      .subscribe(() => this.barCodeFocusSubj.next(undefined));
  }

  private _openCameraSub() {
    return this._peopleSelector
      .eventWhen('open-camera')
      .pipe(takeUntil(this._destroyedSubject))
      .subscribe(async () => await this.openCamera());
  }

  private async _pageSwitchSub() {
    return this.form.pages.activePath$.pipe(takeUntil(this._destroyedSubject)).subscribe(() => {
      this._resetSearchField();
    });
  }

  private _focusBarcodeSubscription() {
    return this._peopleSelector
      .eventWhen('focus-barcode')
      .pipe(takeUntil(this._destroyedSubject))
      .subscribe(() => this.barCodeFocusSubj.next(undefined));
  }

  private _searchToolSwitchSub() {
    return this.form.searchMode$.pipe(takeUntil(this._destroyedSubject)).subscribe(searchMode => {
      if (searchMode === 'barcode') {
        this._peopleSelector.emitEvent('focus-barcode');
      } else if (searchMode === 'keywords') {
        if (this.searchFieldControl.value) this.searchFieldControl.reset('');
        this.keywordsFocusSubj.next(undefined);
      }
    });
  }

  private _fetchSupportedFiltersData() {
    return this.form.pages.activePage$
      .pipe(
        takeUntil(this._destroyedSubject),
        take(1),
        map(({ filters }) => filters),
      )
      .subscribe(filters =>
        filters.forEach(f => {
          switch (f) {
            case 'grades':
              this._gradesService.fetchIfNeeded();
              break;
            case 'roles':
              this._rolesService.fetchIfNeeded();
              break;
            case 'groups':
              this.groupOptions$ = this._groupsFacade.getAllGroups().pipe(
                map(groups =>
                  groups.map(group => ({
                    label: group.name,
                    value: group.hash,
                  })),
                ),
              );
              break;
            default:
              break;
          }
        }),
      );
  }

  public setUserListFilter(val: number[]) {
    this.form.filters.set(
      'lists',
      (val || []).map(v => v.toString()),
    );
  }

  private _peopleSubmitSub() {
    return this._peopleSelector
      .eventWhen('submitted')
      .pipe(takeUntil(this._destroyedSubject))
      .subscribe(() => {
        this._resetSearchField();
      });
  }

  private _resetSearchField() {
    const searchMode = this.form.getCurrentSearchMode();
    if (searchMode === 'barcode') {
      this.barCodeFieldControl.reset();
      this._peopleSelector.emitEvent('focus-barcode');
    } else if (searchMode === 'keywords') {
      if (this.searchFieldControl.value) this.searchFieldControl.reset('');
      this.searchFieldControl.reset();
      this.keywordsFocusSubj.next(undefined);
    }
    this._fetchSupportedFiltersData();
  }

  private _setupFormFiltersObservables() {
    this.filtersFormStructure$ = this._setupFiltersFormStructure();
    this.showMobileFiltersFormButton$ = combineLatest([this.media.isMobileView$, this.filtersFormStructure$]).pipe(
      map(([isMobile, filtersFormStructure]) => {
        if (isMobile) return !isEmpty(filtersFormStructure);
        return false;
      }),
    );
  }

  private _setupFiltersFormStructure() {
    return combineLatest([
      this.form.pages.activePageType$,
      this.form.pages.activePage$.pipe(map(page => page.filters)),
      this.settingsService.isCommunityModuleEnabled(),
      this.groupOptions$,
      this.gradeOptions$,
      this.roleOptions$,
    ]).pipe(
      takeUntil(this._destroyedSubject),
      map(([activePageType, supportedFilters, communityEnabled, groupOptions, gradeOptions, roleOptions]) => {
        const filters: FiltersFormData = {};
        if (activePageType === 'search') {
          for (const supportedFilter of supportedFilters) {
            if (supportedFilter === 'groups' && communityEnabled) {
              filters.group = {
                type: 'multi-select',
                label: 'Group',
                id: 'ps-group-filter',
                options: groupOptions,
              };
            }
            if (supportedFilter === 'grades') {
              filters.grades = {
                type: 'multi-select',
                label: 'Grade',
                id: 'ps-grade-filter',
                options: gradeOptions,
              };
            }
            if (supportedFilter === 'roles') {
              filters.roles = {
                type: 'multi-select',
                label: 'Role',
                id: 'ps-role-filter',
                options: roleOptions,
              };
            }
            if (supportedFilter === 'groups') {
              filters['user-lists'] = {
                type: 'user-list',
                label: 'User list',
                id: 'ps-list-filter',
              };
            }
          }
        }
        return this._filtersFormService.create(filters);
      }),
    );
  }

  /**
   * now that we support QR codes on top of barcodes, the scanenrs
   * will return differently structured data depending on the type of code used
   *
   * Barcode data example - '1234567890'
   * QR code data example - '{"type":"ID","data":{"id":"1234567890"}}'
   */
  private _parseBarcodeScanData(data: string) {
    if (!data) return '';
    if (data.startsWith('{') && data.endsWith('}')) {
      try {
        const parsed = JSON.parse(data);
        if (parsed && parsed?.type === 'ID' && parsed?.data && parsed?.data?.id) {
          return String(parsed.data.id);
        }
      } catch (error) {
        // if it fails to parse, that means it is a normal string. Carry on
        return data;
      }
    }
    return data;
  }
}
